import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { MessageCircle, Mail, QrCode, Globe, Users } from 'lucide-react';
interface SlideTitleProps {
  onContinue: () => void;
}
export function SlideTitle({
  onContinue
}: SlideTitleProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 400);
    const t2 = setTimeout(() => setStage(2), 1200);
    const t3 = setTimeout(() => setStage(3), 2000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);
  const channels = [{
    icon: <MessageCircle className="w-4 h-4" data-id="element-442" />,
    label: 'WhatsApp'
  }, {
    icon: <Mail className="w-4 h-4" data-id="element-443" />,
    label: 'Email'
  }, {
    icon: <QrCode className="w-4 h-4" data-id="element-444" />,
    label: 'QR'
  }, {
    icon: <Globe className="w-4 h-4" data-id="element-445" />,
    label: 'Web'
  }, {
    icon: <Users className="w-4 h-4" data-id="element-446" />,
    label: 'Congress'
  }];
  // 16 West African countries (UN subregion). Coordinates are illustrative
  // positions on a stylized map canvas, not geographically exact.
  const countries = [{
    name: "Côte d'Ivoire",
    x: 32,
    y: 64,
    primary: true
  }, {
    name: 'Nigeria',
    x: 62,
    y: 62,
    hub: true
  }, {
    name: 'Ghana',
    x: 42,
    y: 68
  }, {
    name: 'Senegal',
    x: 10,
    y: 38
  }, {
    name: 'Mali',
    x: 28,
    y: 36
  }, {
    name: 'Burkina Faso',
    x: 38,
    y: 48
  }, {
    name: 'Niger',
    x: 52,
    y: 38
  }, {
    name: 'Benin',
    x: 50,
    y: 60
  }, {
    name: 'Togo',
    x: 46,
    y: 62
  }, {
    name: 'Liberia',
    x: 22,
    y: 70
  }, {
    name: 'Sierra Leone',
    x: 16,
    y: 60
  }, {
    name: 'Guinea',
    x: 18,
    y: 50
  }, {
    name: 'Guinea-Bissau',
    x: 8,
    y: 50
  }, {
    name: 'Gambia',
    x: 6,
    y: 42
  }, {
    name: 'Mauritania',
    x: 16,
    y: 22
  }, {
    name: 'Cabo Verde',
    x: 2,
    y: 30
  }];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto relative" data-id="element-447">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-6" data-id="element-448">
        <span className="px-3 py-1 text-xs uppercase tracking-widest text-teal-400 border border-teal-400/30 rounded-full font-medium" data-id="element-449">
          Snakker for Roche Africa
        </span>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-id="element-450">
        {/* Left: hero copy */}
        <div className="lg:col-span-6 space-y-7" data-id="element-451">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-4xl md:text-6xl font-serif text-white leading-tight" data-id="element-452">
            One portfolio. <br data-id="element-453" />
            <span className="text-teal-400" data-id="element-454">16 markets.</span> <br data-id="element-455" />
            Limited time.
          </motion.h1>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5
        }} className="text-lg text-white/70 leading-relaxed max-w-xl" data-id="element-456">
            Roche Africa doesn't have a content problem. You already have strong
            brands, approved materials and active channels. The challenge is
            making every campaign work across all 16 West African countries —
            consistently and measurably.
          </motion.p>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.7
        }} className="border-l-2 border-teal-400/40 pl-4" data-id="element-457">
            <p className="text-white/40 text-xs uppercase tracking-widest font-medium" data-id="element-458">
              Prepared for
            </p>
            <p className="text-white font-medium mt-1" data-id="element-459">
              Arnaud · Regional Brand Lead, Roche West Africa
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.9
        }} className="pt-2" data-id="element-460">
            <Button onClick={onContinue} className="px-7 py-3.5" data-id="element-461">
              Calculate the pressure →
            </Button>
          </motion.div>
        </div>

        {/* Right: abstract map with country nodes + channel icons */}
        <div className="lg:col-span-6 relative h-[460px]" data-id="element-462">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: stage >= 1 ? 1 : 0
        }} transition={{
          duration: 1
        }} className="absolute inset-0" data-id="element-463">
            <div className="absolute inset-4 bg-navy-900/40 rounded-3xl border border-navy-600 overflow-hidden" data-id="element-464">
              {/* Soft glow centered on West Africa */}
              <div className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-teal-400/10 rounded-full blur-3xl" data-id="element-465" />

              {/* Count badge top-left */}
              <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: stage >= 1 ? 1 : 0,
              y: stage >= 1 ? 0 : -10
            }} className="absolute top-4 left-4 bg-navy-800/80 backdrop-blur-sm border border-teal-400/30 rounded-lg px-3 py-1.5" data-id="element-466">
                <div className="text-[9px] uppercase tracking-widest text-white/50 font-medium" data-id="element-467">
                  West African markets
                </div>
                <div className="text-teal-400 font-serif text-2xl leading-none mt-0.5" data-id="element-468">
                  16
                </div>
              </motion.div>

              {/* Country nodes */}
              {countries.map((c, idx) => <motion.div key={c.name} initial={{
              opacity: 0,
              scale: 0
            }} animate={{
              opacity: stage >= 1 ? 1 : 0,
              scale: stage >= 1 ? 1 : 0
            }} transition={{
              delay: 0.3 + idx * 0.05
            }} className="absolute" style={{
              left: `${c.x}%`,
              top: `${c.y}%`
            }} data-id="element-469">
                  <div className="relative" data-id="element-470">
                    <div className={`rounded-full ${c.primary ? 'w-3.5 h-3.5 bg-teal-400' : c.hub ? 'w-3 h-3 bg-teal-400/70' : 'w-2 h-2 bg-teal-400/40'}`} data-id="element-471" />
                    {c.primary && <div className="absolute inset-0 w-3.5 h-3.5 rounded-full bg-teal-400 animate-ping" data-id="element-472" />}
                    <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-[10px] whitespace-nowrap ${c.primary ? 'text-teal-300 font-medium' : c.hub ? 'text-white/70' : 'text-white/40'}`} data-id="element-473">
                      {c.name}
                    </span>
                  </div>
                </motion.div>)}

              {/* Channel icons floating top-right */}
              <div className="absolute top-4 right-4 flex flex-col gap-2" data-id="element-474">
                {channels.map((ch, idx) => <motion.div key={ch.label} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: stage >= 3 ? 1 : 0,
                x: stage >= 3 ? 0 : 10
              }} transition={{
                delay: idx * 0.08
              }} className="flex items-center gap-2 px-2.5 py-1.5 bg-navy-800/80 backdrop-blur-sm border border-navy-600 rounded-lg text-[10px] text-white/70" data-id="element-475">
                    <span className="text-teal-400" data-id="element-476">{ch.icon}</span>
                    <span data-id="element-477">{ch.label}</span>
                  </motion.div>)}
              </div>

              {/* Bottom legend bar */}
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: stage >= 2 ? 1 : 0,
              y: stage >= 2 ? 0 : 10
            }} className="absolute bottom-4 left-4 right-4 flex items-center gap-4 px-3 py-2 bg-navy-800/60 backdrop-blur-sm border border-navy-600 rounded-lg" data-id="element-478">
                <div className="flex items-center gap-1.5" data-id="element-479">
                  <div className="w-2.5 h-2.5 rounded-full bg-teal-400" data-id="element-480" />
                  <span className="text-[10px] text-white/60" data-id="element-481">Hub</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-482">
                  <div className="w-2 h-2 rounded-full bg-teal-400/70" data-id="element-483" />
                  <span className="text-[10px] text-white/60" data-id="element-484">Key market</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-485">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-400/40" data-id="element-486" />
                  <span className="text-[10px] text-white/60" data-id="element-487">Market</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      <p className="text-white/30 text-xs mt-6 italic" data-id="element-488">
        Illustrative model. Final scope to be aligned with the Roche team.
      </p>
    </div>;
}